import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Notice, Tag } from '../../../common/components/detail/detail';
import { useProjectName } from '../../../global/contexts/lookups/lookups';
import ProjectFilter from '../../projects/layouts/project-filter';
import AuthorizationBadge from '../../tasks/layouts/authorization-badge';
import { WORKFLOW_STATE_LABEL } from '../../tasks/utils/form';
import { BREAKDOWN_STATE_LABEL, TIER_PHASE_LABEL } from '../../tiers/utils/form';
import { VERSION_STATE_LABEL } from '../utils/form';
import { PLANS_PAGE_SIZE, usePlansList } from '../contexts/plans';
import { usePlanMap, useTierTasks } from '../contexts/map';

import '../styles/map.css';

// THE MAP OF A PLAN: plan → version → tiers → tasks, as a cascade.
//
// It answers the question the lists cannot: what came out of what. A plan is
// approved, tiers are built FROM THAT VERSION, and the tasks hang off those
// tiers — and the rule that makes the chain worth drawing is that a tier stays
// under the version it was born from, whatever gets approved later.
//
// EVERY NODE IS A LINK to the screen that owns it, so the map is a way in and
// not a second place to work.
//
// The tasks of a tier load WHEN ITS NODE OPENS, which is also what keeps the
// map cheap on a plan with twenty tiers.

const VERSION_TONE = { approved: 'active', in_review: 'accent', draft: 'warn' };
const BREAKDOWN_TONE = { agreed: 'active', draft: 'warn', legacy: 'neutral' };

export default function PlanMap() {
    const [params, setParams] = useSearchParams();
    const projectId = params.get('project') || '';
    const planId = params.get('plan') || '';
    const projectName = useProjectName();

    const plans = usePlansList({
        limit: PLANS_PAGE_SIZE,
        offset: 0,
        projectId: projectId || undefined,
    });

    const setParam = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        // Changing project drops the plan chosen inside the previous one.
        if (key === 'project') target.delete('plan');
        setParams(target, { replace: true });
    };

    return (
        <div className="plan-map">
            <div className="plan-map__pickers">
                <ProjectFilter value={projectId} onChange={value => setParam('project', value)} />

                <label className="plan-map__field">
                    <span className="plan-map__field-label">Plan</span>
                    <select
                        className="plan-map__select"
                        value={planId}
                        onChange={e => setParam('plan', e.target.value)}
                        disabled={plans.isLoading}
                    >
                        <option value="">Elegí un plan</option>
                        {(plans.data?.items ?? []).map(plan => (
                            <option key={plan.id} value={plan.id}>
                                {plan.name}
                                {!projectId && ` · ${projectName(plan.project_id)}`}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            {!planId
                ? (
                    <Notice>
                        Elegí un plan para ver de qué versión salió cada tier y qué tareas
                        cuelgan de cada uno.
                    </Notice>
                )
                : <Tree planId={planId} />}
        </div>
    );
}

function Tree({ planId }) {
    const { map, isLoading, error, truncated } = usePlanMap(planId);

    if (isLoading) return <p className="plan-map__state">Armando el mapa…</p>;

    if (error) {
        return (
            <p className="plan-map__state plan-map__state--error">
                {error.status === 404
                    ? 'Este plan no está disponible: no existe, o queda fuera de tu alcance.'
                    : error.message || 'No se pudo leer el plan'}
            </p>
        );
    }

    if (!map.plan) return <Notice>No se encontró el plan.</Notice>;

    return (
        <div className="plan-map__tree">
            <div className="plan-map__root">
                <Link to={`/admin/plans/${map.plan.id}`} className="plan-map__plan">
                    {map.plan.name}
                </Link>
                <span className="plan-map__meta">
                    {map.versions.length} {map.versions.length === 1 ? 'versión' : 'versiones'}
                </span>
            </div>

            {truncated && (
                <Notice tone="warn">
                    El proyecto tiene más de 100 tiers y el mapa lee los primeros 100: puede
                    faltar alguno.
                </Notice>
            )}

            <ul className="plan-map__branch">
                {map.versions.map(version => (
                    <VersionNode key={version.id} planId={planId} version={version} />
                ))}
            </ul>

            {map.legacy.length > 0 && (
                <div className="plan-map__legacy">
                    <h4 className="plan-map__legacy-title">
                        Tiers del proyecto sin versión de plan ({map.legacy.length})
                    </h4>
                    <p className="plan-map__note">
                        Son anteriores a la planeación canónica: no salieron de ninguna versión, y
                        no se les atribuye una.
                    </p>
                    <ul className="plan-map__branch">
                        {map.legacy.map(tier => <TierNode key={tier.id} tier={tier} />)}
                    </ul>
                </div>
            )}
        </div>
    );
}

function VersionNode({ planId, version }) {
    // Open by default: a version with tiers is the part of the map somebody
    // came to see, and a tree that starts closed is a tree nobody unfolds.
    return (
        <li className="plan-map__node plan-map__node--version">
            <div className="plan-map__row">
                <Link to={`/admin/plans/${planId}/v/${version.number}`} className="plan-map__title">
                    Versión {version.number}
                </Link>
                <Tag tone={VERSION_TONE[version.state] ?? 'neutral'}>
                    {VERSION_STATE_LABEL[version.state] ?? version.state}
                </Tag>
                <span className="plan-map__meta">
                    {version.tiers.length === 0
                        ? 'sin tiers'
                        : `${version.tiers.length} ${version.tiers.length === 1 ? 'tier' : 'tiers'}`}
                </span>
            </div>

            {version.tiers.length > 0 && (
                <ul className="plan-map__branch">
                    {version.tiers.map(tier => <TierNode key={tier.id} tier={tier} />)}
                </ul>
            )}
        </li>
    );
}

function TierNode({ tier }) {
    const [open, setOpen] = useState(false);
    const tasks = useTierTasks(tier.id, { enabled: open });

    const items = tasks.data?.items ?? [];
    const total = tasks.data?.pagination?.total ?? 0;

    return (
        <li className="plan-map__node plan-map__node--tier">
            <div className="plan-map__row">
                <button
                    type="button"
                    className={`plan-map__toggle${open ? ' plan-map__toggle--open' : ''}`}
                    onClick={() => setOpen(value => !value)}
                    aria-expanded={open}
                    aria-label={open ? `Ocultar las tareas de ${tier.name}` : `Ver las tareas de ${tier.name}`}
                >
                    ▸
                </button>

                <Link to={`/admin/tiers/${tier.id}`} className="plan-map__title">{tier.name}</Link>

                {tier.breakdown_state && (
                    <Tag tone={BREAKDOWN_TONE[tier.breakdown_state] ?? 'neutral'}>
                        {BREAKDOWN_STATE_LABEL[tier.breakdown_state] ?? tier.breakdown_state}
                    </Tag>
                )}
                {tier.phase && (
                    <span className="plan-map__meta">{TIER_PHASE_LABEL[tier.phase] ?? tier.phase}</span>
                )}
                {open && !tasks.isLoading && (
                    <span className="plan-map__meta">
                        {total} {total === 1 ? 'tarea' : 'tareas'}
                    </span>
                )}
            </div>

            {open && (
                <>
                    {tasks.isLoading && <p className="plan-map__state">Leyendo sus tareas…</p>}

                    {tasks.error && (
                        <p className="plan-map__state plan-map__state--error">
                            {tasks.error.message || 'No se pudieron leer las tareas'}
                        </p>
                    )}

                    {!tasks.isLoading && !tasks.error && items.length === 0 && (
                        <p className="plan-map__state">Este tier todavía no tiene tareas.</p>
                    )}

                    {items.length > 0 && (
                        <ul className="plan-map__branch plan-map__branch--tasks">
                            {items.map(task => (
                                <li key={task.id} className="plan-map__node plan-map__node--task">
                                    <div className="plan-map__row">
                                        <Link to={`/admin/tasks/${task.id}`} className="plan-map__title plan-map__title--task">
                                            <span className="plan-map__id">#{task.id}</span> {task.title}
                                        </Link>
                                        <span className={`plan-map__state-chip plan-map__state-chip--${task.workflow_state}`}>
                                            {WORKFLOW_STATE_LABEL[task.workflow_state] ?? task.workflow_state}
                                        </span>
                                        <AuthorizationBadge workflowState={task.workflow_state} compact />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {total > items.length && (
                        <p className="plan-map__note">
                            Se muestran {items.length} de {total}.{' '}
                            <Link to={`/admin/development/tareas?view=list&tier=${tier.id}`}>Verlas en la lista</Link>
                        </p>
                    )}
                </>
            )}
        </li>
    );
}
